import React from "react";
import styled from "styled-components";
import { FatButton, FatButtonInputRightSideProp } from "../__Internal__";
import { ALERT_TYPES } from "../constants/alerts";
import { useHTMLInputElementValidationProjection } from "../hooks/useHTMLInputElementValidationProjection";
import { Icon } from "../Icon";
import { PropError, PropLabel } from "../PropX";
import { style } from "../style";
import { useCheckboxGroupState } from "./CheckboxStateContext";
import { LooseCheckboxButton } from "./LooseCheckboxButton";

type AlertType = "info" | "success" | "warning" | "error" | "loading";

type Props = {
  /**
   * The name of the checkbox field.
   */
  name: string;
  /**
   * The value of the checkbox field. Necessary, but kind of useless for controlled form inputs.
   */
  value: string;

  /**
   * The title of the checkbox.
   */
  title: React.ReactNode;

  /**
   * The subtitle of the checkbox.
   * @default undefined
   */
  subtitle?: React.ReactNode;

  /**
   * The right side of the checkbox.
   * @default undefined
   */
  rightSide?: FatButtonInputRightSideProp;

  /**
   * An optional extension that appears under the checkbox when selected.
   * @default null
   */
  extension?: React.ReactNode;

  /**
   * The content of the alert section to display above the checkbox.
   */
  alert: {
    type: AlertType;
    title?: React.ReactNode;
    description?: React.ReactNode;

    /**
     * The condition on which to display the alert. Defaults to "always".
     */
    showOn?: "checked" | "unchecked" | "always";
  };

  /**
   * An optional icon that appears to the right of the checkbox.
   * @default undefined
   */
  icon?: string;

  /**
   * Whether the checkbox is disabled.
   * @default false
   */
  disabled?: boolean;

  /**
   * Whether the checkbox is required.
   * @default false
   */
  required?: boolean;

  /**
   * Whether the checkbox is checked.
   * @default false
   */
  checked?: boolean;

  /**
   * Whether the checkbox is indeterminate.
   * @default false
   */
  indeterminate?: boolean;

  /**
   * A label to display above the input.
   * @default undefined
   */
  label?: PropLabel;

  /**
   * A custom error to display for the input. Overrides standard errors created by `required`.
   * @default undefined
   */
  error?: PropError | PropError[];

  /**
   * The event for which to validate the input.
   * @default "manual"
   */
  validateOn?: "change" | "blur" | "manual";

  /**
   * When the checkbox changes from checked to unchecked or vice versa.
   */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;

  /**
   * Testing utility tag.
   * @default "checkbox-button-with-alert"
   */
  "data-testid"?: string;
};

const IconWrapper = styled.div`
  line-height: 1;
  min-width: ${style.vars.fontSize.larger};
`;

type AlertBoxProps = {
  $type: AlertType;
  $disabled: boolean;
};

const AlertBox = styled.div<AlertBoxProps>`
  all: unset;
  box-sizing: border-box;
  width: 100%;
  padding: 16px;

  display: flex;
  align-items: flex-start;
  gap: 8px;

  border-top-left-radius: ${style.vars.borderRadius.base};
  border-top-right-radius: ${style.vars.borderRadius.base};
  border: ${style.vars.borderWidth.thin} solid ${(props) => ALERT_TYPES[props.$type].borderColor};
  border-bottom: none;

  background-color: ${(props) => ALERT_TYPES[props.$type].bgColor};
  color: ${style.colors.grey.darkest};
  opacity: ${(props) => (props.$disabled ? 0.3 : 1)};
`;

const AlertTitle = styled.div`
  all: unset;
  box-sizing: border-box;
  font-weight: ${style.vars.fontWeight.bold};
`;

const AlertText = styled.div`
  all: unset;
  box-sizing: border-box;
  font-size: ${style.vars.fontSize.base};
  line-height: ${style.vars.lineHeight};
`;

const shouldShowAlert = (showOn: "always" | "checked" | "unchecked", checked: boolean) => {
  if (showOn === "always") {
    return true;
  }

  if (showOn === "checked" && checked) {
    return true;
  }

  if (showOn === "unchecked" && !checked) {
    return true;
  }

  return false;
};

export const CheckboxButtonWithAlert: React.FC<Props> = (props) => {
  const {
    name,
    value,
    label,
    title,
    alert,
    subtitle,
    rightSide,
    icon,
    required,
    indeterminate,
    extension = null,
    validateOn = "manual",
    disabled = false,
    checked = false,
  } = props;
  const testId = props["data-testid"] ?? "checkbox-button-with-alert";
  const id = `${name}-${value}`;
  const ref = React.useRef<HTMLInputElement>(null);
  const [errors, propsForInput] = useHTMLInputElementValidationProjection(props, { ref, validateOn });

  const { horizontal } = useCheckboxGroupState();

  const showAlert = shouldShowAlert(alert.showOn ?? "always", checked);
  let alertBox: React.ReactNode = null;
  let dashBetweenTitleAndDescription = "";

  if (alert.title) {
    dashBetweenTitleAndDescription = " - ";
  }

  if (showAlert) {
    const alertTitle = typeof alert.title === "string" ? <AlertTitle>{alert.title}</AlertTitle> : alert.title;
    const alertDescription = alert.description ? (
      <>
        {dashBetweenTitleAndDescription} {alert.description}
      </>
    ) : null;

    alertBox = (
      <AlertBox data-testid={`${testId}/alert`} $type={alert.type} $disabled={disabled}>
        <IconWrapper>
          <Icon large icon={ALERT_TYPES[alert.type].icon} />
        </IconWrapper>
        <AlertText>
          {alertTitle}
          {alertDescription}
        </AlertText>
      </AlertBox>
    );
  }

  return (
    <FatButton.BaseContainer $horizontal={horizontal}>
      {label && PropLabel.renderForBeforeInput(label, { htmlFor: id, required: props.required, invalid: propsForInput.invalid })}
      {alertBox}
      <LooseCheckboxButton
        id={id}
        ref={ref}
        data-testid={testId}
        name={name}
        value={value}
        title={title}
        subtitle={subtitle}
        rightSide={rightSide}
        extension={extension}
        icon={icon}
        disabled={disabled}
        required={required}
        checked={checked}
        indeterminate={indeterminate}
        horizontal={horizontal}
        UNSAFE_flatTop={showAlert}
        {...propsForInput}
      />
      {PropError.renderGroupAfterInput(errors)}
    </FatButton.BaseContainer>
  );
};

CheckboxButtonWithAlert.displayName = "CheckboxButtonWithAlert";
